import {ICALENDAR_KEY, SortingProperty} from './constants'
import {getLocalStorage} from '../../../js/util/LocalStorage'
import {departmentNameLower, tzgNameLower, userNameLower} from '../../../js/util/pluralizeUtils'
import messageResource from '../../../js/messageResource'
import GroupType from '../../../js/enums/GroupType'
import UserNameFormat from '../../../js/util/UserNameFormat'
import {convertUsersFilterDependingOnFeatures, nameComparator, numberComparator, stringComparator} from '../../../js/atUtils'

export const isUserSelectionEmpty = ({
    all,
    groups,
    users,
}) => !all && (groups.length + users.length === 0)

export const getTextForUserSelectionDescription = ({
        all,
        groups,
        users,
        groupType,
    },
    emptySelectionMessageKey,
) => {
    if (all) {
        return messageResource.getMessage('my_schedule.users_selector.all_users')
    }

    if (isUserSelectionEmpty({all, groups, users})) {
        return messageResource.getMessage(emptySelectionMessageKey)
    }

    if (groups.length + users.length === 1) {
        return groups.length === 0
               ? formatUserName(users[0])
               : groups[0].name
    }

    return _getMultipleEntitiesDescription(groups, users, groupType)
}

const _getMultipleEntitiesDescription = (groups, users, groupType) => {
    const userGroupNameLower = groupType === GroupType.TIME_ZONE_GROUP ? tzgNameLower : departmentNameLower
    let shortDescription = ''
    if (groups.length > 0) {
        shortDescription += groups.length + ' ' + userGroupNameLower(groups.length)
    }
    if (groups.length > 0 && users.length > 0) {
        shortDescription += ' ' + messageResource.getMessage('common.amp') + ' '
    }
    if (users.length > 0) {
        shortDescription += users.length + ' ' + userNameLower(users.length)
    }
    return shortDescription
}

export const convertData = (feeds, leaveTypes, users, userGroups, timeZoneGroups, recentlyConfiguredId) => {
    return Object.values(feeds).map(({
        creatorId,
        editorId,
        leaveTypesFilter,
        usersFilter,
        sharedUsersFilter,
        ...feed
    }) => {
        return {
            ...feed,
            recentlyConfiguredFeed: recentlyConfiguredId === feed.id,
            leaveTypes: convertLeaveTypes(leaveTypesFilter, leaveTypes),
            userSelection: convertUserSelection(usersFilter, users, userGroups, timeZoneGroups),
            sharedUserSelection: sharedUsersFilter && convertUserSelection(sharedUsersFilter, users, userGroups, timeZoneGroups),
            creator: users[creatorId],
            editor: editorId ? users[editorId] : null,
            createdDate: new Date(feed.createdDate),
            editedDate: feed.editedDate ? new Date(feed.editedDate) : null,
            lastSyncDate: feed.lastSyncDate ? new Date.parseJSONTime(feed.lastSyncDate) : null
        }
    })
}

const convertLeaveTypes = (leaveTypesFilter, leaveTypes) => {
    return leaveTypesFilter.qualifier === window.LeaveTypeSelectionQualifier.ALL
           ? Object.values(leaveTypes)
           : leaveTypesFilter.leaveTypes
                             .filter(leaveTypeId => !!leaveTypes[leaveTypeId])
                             .map(leaveTypeId => leaveTypes[leaveTypeId])
}

const convertUserSelection = (usersFilter, users, userGroups, timezoneGroups) => {
    const UGREnabled = window.SystemSettings.apUGREnabled
    const TZGEnabled = window.SystemSettings.apTZGEnabled

    const usersFilterConverted = convertUsersFilterDependingOnFeatures(usersFilter, UGREnabled, TZGEnabled, Object.values(users))

    const userSelection = {
        all: usersFilterConverted.all,
        groupType: usersFilterConverted.groupType
    }

    if (!usersFilterConverted.all) {
        const groups = usersFilterConverted.groupType === GroupType.TIME_ZONE_GROUP ? timezoneGroups : userGroups
        userSelection.groups = usersFilterConverted.groupIds.map(groupId => groups[groupId]).filter(group => !!group)
        userSelection.users = usersFilterConverted.userIds.map(userId => users[userId]).filter(user => !!user && user.active)
    }
    return userSelection
}

export const formatUserName = user => UserNameFormat.formatFML(user.firstName, user.lastName, user.middleInitial)
const formatUserNameLFM = user => UserNameFormat.formatLFM(user.firstName, user.lastName, user.middleInitial)

const _nullSafeComparator = (firstFeed, secondFeed, getProperty, comparator) => {
    const firstProperty = getProperty(firstFeed)
    const secondProperty = getProperty(secondFeed)
    if (firstProperty && secondProperty) {
        return comparator(firstProperty, secondProperty)
    }

    if (!firstProperty && !secondProperty) {
        let comparisonResult = feedByCreatorNameAscComparator(firstFeed, secondFeed)
        if (comparisonResult === 0) {
            comparisonResult = feedByCreatedDateAscComparator(firstFeed, secondFeed)
        }
        return comparisonResult
    }

    if (firstProperty) {
        return 1
    }

    return -1
}

const feedByNameAscComparator = (firstFeed, secondFeed) => nameComparator(firstFeed, secondFeed)
const feedByCreatorNameAscComparator = (firstFeed, secondFeed) =>
    stringComparator(formatUserNameLFM(firstFeed.creator), formatUserNameLFM(secondFeed.creator))

const feedByEditorNameAscComparator = (firstFeed, secondFeed) =>
    _nullSafeComparator(firstFeed, secondFeed, feed => feed.editor, (firstEditor, secondEditor) =>
        stringComparator(formatUserNameLFM(firstEditor), formatUserNameLFM(secondEditor)))

const feedByCreatedDateAscComparator = (firstFeed, secondFeed) => numberComparator(firstFeed.createdDate, secondFeed.createdDate)
const feedByEditedDateAscComparator = (firstFeed, secondFeed) => _nullSafeComparator(firstFeed, secondFeed, feed => feed.editedDate, numberComparator)
const feedByLastSyncDateAscComparator = (firstFeed, secondFeed) => _nullSafeComparator(firstFeed, secondFeed, feed => feed.lastSyncDate, numberComparator)

const _createComparatorsMap = () => {
    const map = {}
    map[SortingProperty.FEED_NAME] = feedByNameAscComparator
    map[SortingProperty.CREATOR_NAME] = feedByCreatorNameAscComparator
    map[SortingProperty.EDITOR_NAME] = feedByEditorNameAscComparator
    map[SortingProperty.CREATED_DATE] = feedByCreatedDateAscComparator
    map[SortingProperty.EDITED_DATE] = feedByEditedDateAscComparator
    map[SortingProperty.LAST_SYNC] = feedByLastSyncDateAscComparator
    return map
}

const _comparatorsMap = _createComparatorsMap()

export const sortFeedsBySetting = (feeds, sorting) => {
    return feeds.sort(_comparatorsMap[sorting.property])
}

export const getICalInfoFromLocalStorage = () => {
    return getLocalStorage().getItem(ICALENDAR_KEY)
}

export const saveICalInfoToLocalStorage = ({activeTab, allSorting, sharedSorting}) => {
    getLocalStorage().setItem(ICALENDAR_KEY,
        {
            activeTab,
            allSorting,
            sharedSorting,
        })
}

let defaultDateRange = null

export const getDefaultDateRange = () => {
    if (!defaultDateRange) {
        defaultDateRange = {
            qualifier: window.DateRangeSelectionQualifier.CURRENT_YEAR,
            fromDate: window.AT.CurrentYearDateRangeStateInstance.getFromDate(),
            toDate: window.AT.CurrentYearDateRangeStateInstance.getToDate(),
        }
    }

    return defaultDateRange
}



// WEBPACK FOOTER //
// ./web/actiplans/js/icalendar/utils.js